/**
 * Espace partenaire — Mes interventions (kanban). Le partenaire suit ses
 * dossiers sous-traités dans le même pipeline que BCL, jusqu'à la facturation :
 * À réaliser → Rapport déposé → (À corriger) → À facturer → Facturé. Il dépose
 * son rapport puis sa facture ; BCL décide ensuite du règlement.
 */
import type { PartnerInvoice } from '@bcl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { uploadToPresignedUrl } from '@/features/documents/documents-api';
import { extractErrorMessage } from '@/features/auth/error-message';
import { interventionStatusBadge } from '@/features/partners/intervention-labels';
import {
  listMyInterventions,
  submitInterventionReport,
} from '@/features/partners/interventions-api';
import {
  listMyInvoices,
  requestInvoiceUpload,
} from '@/features/partners/partner-invoices-api';

type ColumnKey =
  | 'a_realiser'
  | 'rapport_depose'
  | 'a_corriger'
  | 'a_facturer'
  | 'facture';

const COLUMNS: { key: ColumnKey; label: string; cls: string }[] = [
  { key: 'a_realiser', label: 'À réaliser', cls: 'bg-amber-100 text-amber-800' },
  {
    key: 'rapport_depose',
    label: 'Rapport déposé',
    cls: 'bg-sky-100 text-sky-800',
  },
  { key: 'a_corriger', label: 'À corriger', cls: 'bg-red-100 text-red-800' },
  {
    key: 'a_facturer',
    label: 'À facturer',
    cls: 'bg-violet-100 text-violet-800',
  },
  { key: 'facture', label: 'Facturé', cls: 'bg-green-100 text-green-800' },
];

/** Colonne d'une intervention selon son statut + l'état de la facture. */
function columnOf(status: string, invoice: PartnerInvoice | undefined): ColumnKey | null {
  switch (status) {
    case 'assignee':
      return 'a_realiser';
    case 'rapport_depose':
      return 'rapport_depose';
    case 'rejetee':
    case 'litige':
      return 'a_corriger';
    case 'validee':
      return invoice?.status === 'paye' ? 'facture' : 'a_facturer';
    default:
      return null;
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR');
}

export function PartnerInterventionsPage() {
  const qc = useQueryClient();
  const data = useQuery({
    queryKey: ['partner', 'interventions'],
    queryFn: listMyInterventions,
  });
  const invoices = useQuery({
    queryKey: ['partner', 'invoices'],
    queryFn: listMyInvoices,
  });

  const invoiceByIntervention = new Map<string, PartnerInvoice>();
  for (const inv of invoices.data ?? []) {
    invoiceByIntervention.set(inv.interventionId, inv);
  }

  const [reportFor, setReportFor] = useState<string | null>(null);
  const [report, setReport] = useState('');

  const [invoiceFor, setInvoiceFor] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const submitMut = useMutation({
    mutationFn: () => submitInterventionReport(reportFor!, report),
    onSuccess: () => {
      toast.success('Rapport déposé');
      setReportFor(null);
      setReport('');
      qc.invalidateQueries({ queryKey: ['partner', 'interventions'] });
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const invoiceMut = useMutation({
    mutationFn: async (file: File) => {
      const { uploadUrl } = await requestInvoiceUpload({
        interventionId: invoiceFor!,
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        amount: Number(amount),
      });
      setUploadProgress(0);
      await uploadToPresignedUrl(uploadUrl, file, setUploadProgress);
    },
    onSuccess: () => {
      toast.success('Facture déposée');
      setInvoiceFor(null);
      setAmount('');
      if (fileRef.current) fileRef.current.value = '';
      qc.invalidateQueries({ queryKey: ['partner', 'invoices'] });
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
    onSettled: () => setUploadProgress(null),
  });

  const items = data.data ?? [];

  return (
    <DashboardLayout variant="partenaire" title="Mes interventions">
      <Card>
        <CardHeader>
          <CardTitle>Interventions sous-traitées</CardTitle>
          <CardDescription>
            Votre pipeline jusqu'à la facturation. Déposez votre rapport, puis
            votre facture une fois le dossier validé.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 overflow-x-auto pb-3">
            {COLUMNS.map((col) => {
              const cards = items.filter(
                (it) =>
                  columnOf(it.status, invoiceByIntervention.get(it.id)) ===
                  col.key,
              );
              return (
                <div key={col.key} className="w-72 shrink-0">
                  <div className="mb-2 flex items-center justify-between">
                    <Badge variant="outline" className={col.cls}>
                      {col.label}
                    </Badge>
                    <span className="text-xs text-gray-500">{cards.length}</span>
                  </div>
                  <div className="space-y-2">
                    {cards.map((it) => {
                      const invoice = invoiceByIntervention.get(it.id);
                      return (
                        <div
                          key={it.id}
                          className="space-y-2 rounded-md border bg-white p-3 text-sm"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold">
                              {it.projectReference ?? '—'}
                            </span>
                            <Badge
                              variant="outline"
                              className={`shrink-0 text-[10px] ${interventionStatusBadge[it.status].cls}`}
                            >
                              {interventionStatusBadge[it.status].label}
                            </Badge>
                          </div>
                          {it.notes && (
                            <p className="text-xs text-gray-500">{it.notes}</p>
                          )}
                          {it.scheduledAt && (
                            <p className="text-xs text-gray-600">
                              📅 {formatDate(it.scheduledAt)}
                            </p>
                          )}

                          {col.key === 'a_realiser' && (
                            <Button
                              size="sm"
                              className="w-full"
                              onClick={() => {
                                setReportFor(it.id);
                                setReport('');
                              }}
                            >
                              Déposer le rapport
                            </Button>
                          )}

                          {col.key === 'rapport_depose' && (
                            <p className="text-xs text-sky-700">
                              En attente de validation BCL
                            </p>
                          )}

                          {col.key === 'a_corriger' && it.reviewReason && (
                            <p className="text-xs text-red-600">
                              Motif : {it.reviewReason}
                            </p>
                          )}

                          {col.key === 'a_facturer' &&
                            (invoice ? (
                              <p className="text-xs text-violet-700">
                                Facture déposée ({(invoice.amountCents / 100).toFixed(2)} €)
                                — en attente de règlement BCL
                              </p>
                            ) : (
                              <Button
                                size="sm"
                                className="w-full"
                                onClick={() => {
                                  setInvoiceFor(it.id);
                                  setAmount('');
                                }}
                              >
                                Déposer la facture
                              </Button>
                            ))}

                          {col.key === 'facture' && invoice && (
                            <p className="text-xs font-medium text-green-700">
                              ✓ Réglée — {(invoice.amountCents / 100).toFixed(2)} €
                            </p>
                          )}
                        </div>
                      );
                    })}
                    {cards.length === 0 && (
                      <p className="rounded-md border border-dashed p-3 text-center text-xs text-gray-300">
                        —
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Dialog open={reportFor !== null} onOpenChange={(o) => !o && setReportFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rapport d'intervention</DialogTitle>
            <DialogDescription>
              Décrivez l'intervention réalisée. BCL la validera pour paiement.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={6}
            value={report}
            onChange={(e) => setReport(e.target.value)}
            placeholder="Compte rendu de l'intervention…"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportFor(null)}>
              Annuler
            </Button>
            <Button
              disabled={!report.trim() || submitMut.isPending}
              onClick={() => submitMut.mutate()}
            >
              {submitMut.isPending ? 'Envoi…' : 'Déposer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={invoiceFor !== null} onOpenChange={(o) => !o && setInvoiceFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Déposer ma facture</DialogTitle>
            <DialogDescription>
              Indiquez le montant TTC et joignez votre facture. BCL procédera au
              paiement.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="amount">Montant TTC (€)</Label>
            <Input
              id="amount"
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) invoiceMut.mutate(file);
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceFor(null)}>
              Annuler
            </Button>
            <Button
              disabled={!amount || Number(amount) <= 0 || invoiceMut.isPending}
              onClick={() => fileRef.current?.click()}
            >
              {uploadProgress !== null
                ? `Envoi… ${uploadProgress}%`
                : 'Choisir le fichier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
